/* Name-necklace illustration: nameplate pendant on a fine chain.
 * Reuses NB.fonts (bracelet-render.js) and NB.config.colors palettes.
 * PLACEHOLDER proportions: pendant size, chain style and ring placement are
 * illustrative only until the CJ necklace spec is confirmed.
 */
(function () {
  'use strict';

  const NB = window.NB = window.NB || {};
  const NS = 'http://www.w3.org/2000/svg';
  const W = 800, H = 420;
  const CX = 400, CY = 292;          // pendant centre
  const MAX_W = 430;                 // max name width (SVG units)
  const TARGET_H = 66, CAP_H = 74;   // ink-height target / cap
  let serial = 0;

  const ornaments = {
    crown: 'M-14 6L-16-10L-7-2L0-14L7-2L16-10L14 6Z',
    heart: 'M0 9C-14 0-14-12-6-12C-2-12 0-9 0-7C0-9 2-12 6-12C14-12 14 0 0 9Z'
  };

  function node(tag, attributes = {}, parent) {
    const result = document.createElementNS(NS, tag);
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== null && value !== undefined) result.setAttribute(key, value);
    });
    if (parent) parent.appendChild(result);
    return result;
  }

  function gradient(parent, id, stops) {
    const result = node('linearGradient', {
      id, x1: 0, y1: 0, x2: 0.15, y2: 1
    }, parent);
    stops.forEach(([offset, color]) => node('stop', {
      offset, 'stop-color': `var(--m-${color})`
    }, result));
  }

  // Quadratic Bezier sampled at roughly equal arc-length spacing.
  function along(p0, p1, p2, spacing) {
    const pts = [];
    const at = t => ({
      x: (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * p1.x + t * t * p2.x,
      y: (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * p1.y + t * t * p2.y
    });
    let prev = at(0), acc = spacing;
    for (let i = 1; i <= 600; i++) {
      const p = at(i / 600);
      acc += Math.hypot(p.x - prev.x, p.y - prev.y);
      if (acc >= spacing) {
        acc = 0;
        pts.push({ ...p, a: Math.atan2(p.y - prev.y, p.x - prev.x) * 180 / Math.PI });
      }
      prev = p;
    }
    return pts;
  }

  class Necklace {
    constructor(svg, options = {}) {
      this.svg = svg;
      this.opts = { shadow: true, ...options };
      this.uid = 'nbn' + (++serial);
      this.state = null;
      this.destroyed = false;
      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
      svg.setAttribute('role', 'img');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.classList.add('nb-necklace');
      this._defs();
    }

    _defs() {
      const u = this.uid;
      this.svg.replaceChildren();
      const defs = node('defs', {}, this.svg);
      gradient(defs, u + '-link', [
        ['0', 'hi'], ['.3', 'light'], ['.55', 'mid'], ['.8', 'dark'], ['1', 'light']
      ]);
      gradient(defs, u + '-face', [
        ['0', 'hi'], ['.3', 'light'], ['.55', 'mid'], ['.75', 'light'], ['1', 'dark']
      ]);
      gradient(defs, u + '-side', [['0', 'edge'], ['1', 'dark']]);
      const shadow = node('filter', {
        id: u + '-shadow', x: '-10%', y: '-30%', width: '120%', height: '170%'
      }, defs);
      node('feDropShadow', {
        dx: 0, dy: 4, stdDeviation: 3,
        'flood-color': '#20160a', 'flood-opacity': '.2'
      }, shadow);
      this.guide = node('g', { class: 'nb-necklace__guide' }, this.svg);
      this.root = node('g', {
        class: 'nb-necklace__root',
        filter: this.opts.shadow ? `url(#${u}-shadow)` : null
      }, this.svg);
    }

    setColor(key) {
      const colors = NB.config.colors;
      const color = colors[key] || colors.gold;
      Object.entries(color.m).forEach(([name, value]) => {
        this.svg.style.setProperty('--m-' + name, value);
      });
    }

    /* source: { name, font, palette } — palette is an NB.config.colors key. */
    render(source) {
      if (this.destroyed) return;
      const state = {
        name: String(source.name ?? ''),
        font: source.font || '03',
        palette: source.palette || 'gold'
      };
      this.state = state;
      const font = NB.util.fontById(state.font);
      this.setColor(state.palette);
      this._draw(state, font);
      if (!NB.fonts.isLoaded(font)) {
        NB.fonts.ensure(font).then(ok => {
          if (!this.destroyed && this.state === state) {
            this.svg.dataset.fontLoaded = String(ok);
            this._draw(state, font);
          }
        });
      }
      this.svg.setAttribute('aria-label',
        window.CariamText("copy.text_256", "ネックレスの参考プレビュー。名前：[v0]。書体 #[v1]（参考表示）。", {v0: state.name || window.CariamText("copy.text_229", '未入力'), v1: font.id}));
    }

    _link(parent, p, index) {
      const g = node('g', {
        transform: `translate(${p.x.toFixed(2)} ${p.y.toFixed(2)}) rotate(${p.a.toFixed(2)})`
      }, parent);
      const flat = index % 2 === 1;
      node('ellipse', {
        rx: 6.4, ry: flat ? 1.5 : 3.6, fill: 'none',
        stroke: 'var(--m-dark)', 'stroke-width': flat ? 2.6 : 2.4
      }, g);
      node('ellipse', {
        rx: 6.4, ry: flat ? 1.5 : 3.6, fill: 'none',
        stroke: `url(#${this.uid}-link)`, 'stroke-width': flat ? 1.6 : 1.5
      }, g);
    }

    _draw(state, font) {
      const u = this.uid;
      this.root.replaceChildren();
      const text = state.name;
      const target = font.orn ? TARGET_H - 10 : TARGET_H;
      const probe = NB.fonts.metrics(text || 'Name', font, 64);
      let size = Math.min(110, 64 * target / probe.height) * Math.min(1, font.scale || 1);
      let metrics = NB.fonts.metrics(text || 'Name', font, size);
      if (metrics.width > MAX_W) {
        size *= MAX_W / metrics.width;
        metrics = NB.fonts.metrics(text || 'Name', font, size);
      }
      if (metrics.height > CAP_H) {
        size *= CAP_H / metrics.height;
        metrics = NB.fonts.metrics(text || 'Name', font, size);
      }
      const width = Math.max(40, metrics.width + 6);
      const ringR = 5.5;
      const left = { x: CX - width / 2 - ringR - 1, y: CY - 4 };
      const right = { x: CX + width / 2 + ringR + 1, y: CY - 4 };

      // Faint neckline guide (illustration context only).
      this.guide.replaceChildren();
      node('path', {
        d: `M235 -5C250 120 320 205 ${CX} 215C480 205 550 120 565 -5`,
        fill: 'none', stroke: '#e3d9c8', 'stroke-width': 1.5,
        'stroke-dasharray': '2 6', 'stroke-linecap': 'round'
      }, this.guide);

      const chain = node('g', { class: 'nb-necklace__chain' }, this.root);
      const halves = [
        [{ x: 150, y: -6 }, { x: 175, y: CY - 30 }, { x: left.x - ringR, y: left.y }],
        [{ x: 650, y: -6 }, { x: 625, y: CY - 30 }, { x: right.x + ringR, y: right.y }]
      ];
      let index = 0;
      halves.forEach(([a, b, c]) => {
        along(a, b, c, 10.5).forEach(p => this._link(chain, p, index++));
      });

      [left, right].forEach(p => {
        node('circle', {
          cx: p.x, cy: p.y, r: ringR, fill: 'none',
          stroke: 'var(--m-dark)', 'stroke-width': 3.2
        }, this.root);
        node('circle', {
          cx: p.x, cy: p.y, r: ringR, fill: 'none',
          stroke: `url(#${u}-link)`, 'stroke-width': 2
        }, this.root);
      });

      const group = node('g', {
        class: 'nb-necklace__name',
        transform: `translate(${CX} ${CY})`,
        'data-letter-height': metrics.height.toFixed(2)
      }, this.root);
      const css = NB.fonts.css(font);
      const baseline = (metrics.ascent - metrics.descent) / 2;
      const attrs = {
        x: 0, y: baseline, 'text-anchor': 'middle',
        'font-family': css.fontFamily, 'font-weight': css.fontWeight,
        'font-style': css.fontStyle, 'font-size': size.toFixed(3),
        'stroke-linejoin': 'round'
      };

      if (!text) {
        node('text', {
          ...attrs, fill: 'none', stroke: 'var(--m-mid)',
          'stroke-width': 1, 'stroke-dasharray': '3 3', opacity: .6
        }, group).textContent = window.CariamText("copy.text_231", '名前');
        return;
      }
      node('text', {
        ...attrs, y: baseline + 1.4, fill: `url(#${u}-side)`,
        stroke: 'var(--m-dark)', 'stroke-width': 1.2
      }, group).textContent = text;
      node('text', {
        ...attrs, class: 'nb-necklace__face', fill: `url(#${u}-face)`,
        stroke: 'var(--m-edge)', 'stroke-width': .7, 'paint-order': 'stroke'
      }, group).textContent = text;
      node('text', {
        ...attrs, y: baseline - .35, fill: 'none', stroke: 'var(--m-hi)',
        'stroke-width': .35, opacity: .45
      }, group).textContent = text;

      const inkTop = -metrics.height / 2;
      if (ornaments[font.orn]) {
        const ox = font.orn === 'crown' ? -width / 2 + 14 : width / 2 - 8;
        node('path', {
          d: ornaments[font.orn],
          transform: `translate(${ox} ${inkTop - 3}) scale(.7)`,
          fill: `url(#${u}-face)`, stroke: 'var(--m-edge)', 'stroke-width': .8
        }, group);
      } else if (font.orn === 'rings') {
        [-1, 1].forEach(d => node('circle', {
          cx: d * (width / 2 - 4), cy: inkTop + 3, r: 5, fill: 'none',
          stroke: `url(#${u}-face)`, 'stroke-width': 2.2
        }, group));
      }
    }

    pulse() {
      if (this.destroyed || !window.gsap || NB.util.reducedMotion()) return;
      const name = this.root.querySelector('.nb-necklace__name');
      if (name) gsap.fromTo(name, { opacity: .65 }, { opacity: 1, duration: .25 });
    }

    destroy() {
      this.destroyed = true;
    }
  }

  NB.Necklace = Necklace;
})();
